/**
 * useTeacherDashboardFeatures - Reads the three teacher dashboard toggles from
 * the backend: `feature.teacher_radar`, `feature.teacher_analytics_panel` and
 * `feature.teacher_ai_tools`.
 *
 * Fail-open: a flag is `false` ONLY when the backend explicitly confirms
 * `value === false`. Loading, unauthenticated, absent key, error → `true`.
 * These are default-on surfaces, so anything short of an explicit opt-out keeps
 * them visible (opposite of `useTeacherPlanEnabled`, which is fail-closed).
 *
 * The three keys are fetched together in a single multi-key request, once per
 * authenticated session and shared across all consumers. The cache is scoped to
 * the logged-in user via `createSessionConfigHook`: nothing is fetched or
 * cached while logged out, and an identity change drops the cache and refetches
 * so one user's tenant flags are never served to another.
 *
 * Audience: the request only fires for AI-enabled teachers (`isPersistedAiTeacher`)
 * — the only users these flags gate anything for. Students, non-AI teachers and
 * other roles get the fail-open defaults without any network traffic.
 *
 * Returns an object keyed by the backend config keys in `TEACHER_DASHBOARD_FEATURES`;
 * read each flag via that enum, e.g. `features[TEACHER_DASHBOARD_FEATURES.RADAR]`.
 *
 * @returns {Record<string, boolean>}
 */
import { TEACHER_DASHBOARD_FEATURES } from '../constants/teacherDashboardFeatures';
import configurationsService from '../services/configurationsService';
import { isPersistedAiTeacher } from '../services/serviceConfig';
import { createSessionConfigHook } from './createSessionConfigHook';

const ALL_ENABLED = {
  [TEACHER_DASHBOARD_FEATURES.RADAR]: true,
  [TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL]: true,
  [TEACHER_DASHBOARD_FEATURES.AI_TOOLS]: true,
};

function resolveFlag(result) {
  return !(result.status === 'ok' && result.value === false);
}

function fetchFeatures() {
  return configurationsService
    .getConfigurations([
      TEACHER_DASHBOARD_FEATURES.RADAR,
      TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL,
      TEACHER_DASHBOARD_FEATURES.AI_TOOLS,
    ])
    .then((results) => ({
      [TEACHER_DASHBOARD_FEATURES.RADAR]: resolveFlag(results[TEACHER_DASHBOARD_FEATURES.RADAR]),
      [TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL]: resolveFlag(results[TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL]),
      [TEACHER_DASHBOARD_FEATURES.AI_TOOLS]: resolveFlag(results[TEACHER_DASHBOARD_FEATURES.AI_TOOLS]),
    }));
}

const useTeacherDashboardFeatures = createSessionConfigHook({
  fetchValue: fetchFeatures,
  defaultValue: ALL_ENABLED,
  isEligible: isPersistedAiTeacher,
});

export default useTeacherDashboardFeatures;
